<template>
  <div class="lexical-editor">
    <div ref="editorRoot" class="lexical-editor__root" contenteditable="true" role="textbox" spellcheck="true"
      tabindex="0"></div>
  </div>
</template>

<script lang="ts" setup>
import { createEmptyHistoryState, registerHistory } from "@lexical/history";
import { $isMarkNode, MarkNode } from "@lexical/mark";
import { registerRichText } from "@lexical/rich-text";
import {
  $createProofreadingMarkNode as createProofreadingMarkNodeForReplace,
  ProofreadingMarkNode,
} from "@/lib/lexical/ProofreadingMarkNode";
import {
  applySuggestionAtMark as applySuggestionAtMarkOp,
  buildMarkStatusById,
  collectMarkNodesById,
  getEditorMarkStartOffsets,
  getEditorPlainText,
  moveCursorToMarkStart as moveCursorToMarkStartOp,
  moveCursorToUtf16Offset as moveCursorToUtf16OffsetOp,
  paintMarkDomAppearance,
  revertSuggestionAtMark as revertSuggestionAtMarkOp,
  replaceTextAtUtf16Range as replaceTextAtUtf16RangeOp,
  updateMarkItems,
} from "@/lib/lexical/proofreadingMarkOps";
import {
  $getNearestNodeFromDOMNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  createEditor,
  INSERT_LINE_BREAK_COMMAND,
  KEY_ENTER_COMMAND,
  mergeRegister,
  type LexicalEditor as LexicalEditorInstance,
  type LexicalNode,
} from "lexical";
import {
  scrollIntoView,
  SCROLL_INTO_VIEW_DURATION_MS,
} from "@/lib/utils/proofreadingEditorUtil";
import { lineWordToOffset } from "@/lib/utils/proofreadingPositions";
import type { MarkItem, ProofreadingIssueStatus } from "@/types/proofreading";
import { defineEmits, defineExpose, defineProps, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps<{
  content: string;
}>();

const emit = defineEmits<{
  (event: "mark-click", markIds: string[] | null): void;
  (event: "content-change"): void;
  (event: "editor-ready"): void;
}>();

const EDITOR_NAMESPACE = "ProofreadingLexicalEditor";
const HISTORY_MERGE_DELAY_MS = 300;

const editorRoot = ref<HTMLDivElement | null>(null);
let editor: LexicalEditorInstance | null = null;
let unregisterPlugins: (() => void) | null = null;
let onEditorRootClick: ((event: MouseEvent) => void) | null = null;
let suppressContentChange = 0;
let isInitialUpdate = true;
let markStatusById = new Map<string, ProofreadingIssueStatus>();
let hoveredMarkId: string | null = null;

function runWithoutContentChange(fn: () => void) {
  suppressContentChange += 1;
  try {
    fn();
  } finally {
    queueMicrotask(() => {
      suppressContentChange -= 1;
    });
  }
}

function paintCurrentMarkAppearance() {
  if (!editor) return;
  paintMarkDomAppearance(editor, markStatusById, hoveredMarkId);
}

function updateMarkStatusById(markItems: MarkItem[]) {
  markStatusById = buildMarkStatusById(markItems);
}

function handleEditorRootClick(event: MouseEvent) {
  if (!editor) return;

  const { target } = event;
  if (!(target instanceof Node)) return;

  editor.getEditorState().read(() => {
    const lexicalNode = $getNearestNodeFromDOMNode(target);
    if (!lexicalNode) {
      emit("mark-click", null);
      return;
    }

    const markIds = collectMarkIdsAlongAncestors(lexicalNode);
    emit("mark-click", markIds.length > 0 ? markIds : null);
  }, { editor });
}

function collectMarkIdsAlongAncestors(node: LexicalNode): string[] {
  const ids: string[] = [];
  let current: LexicalNode | null = $isMarkNode(node) ? node : node.getParent();

  while (current != null) {
    if ($isMarkNode(current)) {
      current.getIDs().forEach((id) => {
        if (!ids.includes(id)) {
          ids.push(id);
        }
      });
    }
    current = current.getParent();
  }

  return ids;
}

function getEditorScrollContainer(): HTMLElement | null {
  const parent = editorRoot.value?.parentElement;
  return parent instanceof HTMLElement ? parent : null;
}

function restoreEditorScrollTop(scrollTop: number) {
  const container = getEditorScrollContainer();
  if (container == null) return;

  const apply = () => {
    container.scrollTop = scrollTop;
  };

  apply();
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(() => {
      apply();
      nextTick(() => {
        apply();
        requestAnimationFrame(apply);
      });
    });
  });
}

function withPreservedEditorScroll(fn: () => void) {
  const scrollContainer = getEditorScrollContainer();
  const savedScrollTop = scrollContainer?.scrollTop ?? null;
  fn();
  if (savedScrollTop != null) {
    restoreEditorScrollTop(savedScrollTop);
  }
}


defineExpose({
  getPlainText() {
    if (!editor) return "";
    return getEditorPlainText(editor);
  },
  applyMarkItems(markItems: MarkItem[], contentOverride?: string) {
    if (editor) {
      updateMarkStatusById(markItems);
      runWithoutContentChange(() => {
        withPreservedEditorScroll(() => {
          const editorText = getEditorPlainText(editor!);
          let { content } = props;
          if (contentOverride != null) {
            content = contentOverride;
          } else if (editorText.length > 0) {
            content = editorText;
          }
          updateMarkItems(editor!, content, markItems);
        });
      });
    }
  },
  getMarkStartOffset(markId: string): number | null {
    if (!editor) return null;
    return getEditorMarkStartOffsets(editor).get(markId) ?? null;
  },
  hasAnyMark(): boolean {
    if (!editor) return false;
    return getEditorMarkStartOffsets(editor).size > 0;
  },
  syncMarkItemStatuses(markItems: MarkItem[]) {
    updateMarkStatusById(markItems);
    paintCurrentMarkAppearance();
  },
  moveCursorToMarkStart(markId: string) {
    if (!editor) return false;

    const moved = moveCursorToMarkStartOp(editor, markId);
    if (!moved) return false;

    editorRoot.value?.focus({ preventScroll: true });

    requestAnimationFrame(() => {
      editor?.getEditorState().read(() => {
        const markNodes: MarkNode[] = [];
        $getRoot().getChildren().forEach((child) => collectMarkNodesById(child, markId, markNodes));
        const element = editor?.getElementByKey(markNodes[0]?.getKey());
        if (element instanceof HTMLElement) {
          scrollIntoView(element, SCROLL_INTO_VIEW_DURATION_MS);
        }
      });
    });

    return true;
  },
  moveCursorToLineWord(line: number, column: number) {
    if (!editor) return false;

    const offset = lineWordToOffset(getEditorPlainText(editor), line, column);
    if (offset === null) return false;

    const textNodeKey = moveCursorToUtf16OffsetOp(editor, offset);
    if (textNodeKey === null) return false;

    editorRoot.value?.focus({ preventScroll: true });

    requestAnimationFrame(() => {
      const element = editor?.getElementByKey(textNodeKey);
      if (element instanceof HTMLElement) {
        scrollIntoView(element, SCROLL_INTO_VIEW_DURATION_MS);
      }
    });

    return true;
  },
  applySuggestionAtMark(markId: string, replacementText: string) {
    if (!editor) return false;
    markStatusById.set(markId, "applied");
    let replaced = false;
    runWithoutContentChange(() => {
      withPreservedEditorScroll(() => {
        replaced = applySuggestionAtMarkOp(editor!, markId, replacementText);
      });
    });
    return replaced;
  },
  revertSuggestionAtMark(markId: string, originalText: string) {
    if (!editor) return false;
    markStatusById.set(markId, "pending");
    let reverted = false;
    runWithoutContentChange(() => {
      withPreservedEditorScroll(() => {
        reverted = revertSuggestionAtMarkOp(editor!, markId, originalText);
      });
    });
    return reverted;
  },
  replaceTextAtUtf16Range(
    startUtf16: number,
    oldUtf16Len: number,
    newText: string,
    expectedOldText?: string,
  ) {
    if (!editor) return false;
    let replaced = false;
    runWithoutContentChange(() => {
      withPreservedEditorScroll(() => {
        replaced = replaceTextAtUtf16RangeOp(
          editor!,
          startUtf16,
          oldUtf16Len,
          newText,
          expectedOldText,
        );
      });
    });
    return replaced;
  },
  setHoveredMarkId(markId: string | null) {
    hoveredMarkId = markId;
    paintCurrentMarkAppearance();
  },
  onCompleteProofreading() {
    // エディタの先頭にスクロールする
    nextTick(() => {
      const domElement = editor!.getElementByKey("root");
      if (domElement !== null) {
        domElement.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    });
  },
});

onMounted(() => {
  if (!editorRoot.value) return;

  const historyState = createEmptyHistoryState();

  editor = createEditor({
    namespace: EDITOR_NAMESPACE,
    editable: true,
    nodes: [
      ProofreadingMarkNode,
      {
        replace: MarkNode,
        with: (node) => createProofreadingMarkNodeForReplace(node.getIDs()),
        withKlass: ProofreadingMarkNode,
      },
    ],
    theme: {
      paragraph: "lexical-editor__paragraph",
      mark: "lexical-editor__mark",
    },
    onError(error: Error) {
      throw error;
    },
  });

  editor.setRootElement(editorRoot.value);
  unregisterPlugins = mergeRegister(
    registerRichText(editor),
    registerHistory(editor, historyState, HISTORY_MERGE_DELAY_MS),
    editor.registerUpdateListener(() => {
      paintCurrentMarkAppearance();

      if (isInitialUpdate) {
        isInitialUpdate = false;
        return;
      }
      if (suppressContentChange > 0) return;
      emit("content-change");
    }),
    editor.registerCommand(KEY_ENTER_COMMAND, (event) => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) {
        return false;
      }

      if (event !== null) {
        event.preventDefault();
      }

      // 常にラインブレイクを挿入  
      return editor!.dispatchCommand(INSERT_LINE_BREAK_COMMAND, false);
    }, COMMAND_PRIORITY_HIGH)

  );

  runWithoutContentChange(() => {
    updateMarkItems(editor!, props.content, []);
  });
  emit("editor-ready");

  onEditorRootClick = (event: MouseEvent) => {
    handleEditorRootClick(event);
  };
  editorRoot.value.addEventListener("click", onEditorRootClick);
});

onBeforeUnmount(() => {
  if (editorRoot.value && onEditorRootClick) {
    editorRoot.value.removeEventListener("click", onEditorRootClick);
    onEditorRootClick = null;
  }
  if (unregisterPlugins) {
    unregisterPlugins();
    unregisterPlugins = null;
  }
  if (editor) {
    editor.setRootElement(null);
    editor = null;
  }
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.lexical-editor {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  height: 100%;

  box-sizing: border-box;
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: auto;
  overflow-anchor: none;
  content-visibility: auto;

  @include mq(sp) {
    border-radius: 0;
  }
}

.lexical-editor__root {
  min-height: 100%;
  outline: none;
  cursor: text;
  font-size: var(--font-size-md);
  line-height: 2;
  color: var(--color-text-normal);
  user-select: text;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 16px;

  &:focus {
    outline: none;
  }
}

::v-deep .lexical-editor__paragraph {
  margin: 0;
  text-indent: 0;
}

::v-deep .lexical-editor__mark {
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-thickness: 2.5px;
  text-underline-offset: 8px;
}

::v-deep .lexical-editor__mark>.lexical-editor__mark {
  text-underline-offset: 12px;
}

::v-deep .lexical-editor__mark--pending {
  text-decoration-color: var(--color-red-2);

  &:hover {
    text-decoration-color: var(--color-red-1);
  }
}

::v-deep .lexical-editor__mark--rejected {
  text-decoration: none;
}

::v-deep .lexical-editor__mark--applied {
  text-decoration: none;
}

::v-deep .lexical-editor__mark--hovered {
  background-color: color-mix(in srgb, var(--color-red-1) 50%, transparent);
  border-radius: 2px;
  text-decoration-color: var(--color-red-1);
}
</style>
